import type { MouseEvent, ReactNode } from "react";
import { isPlainLeftClick } from "../../clicks";
import { Icon } from "../../icons";
import { useTooltip } from "../Tooltip/Tooltip";
import type { NavItem } from "./navTypes";

interface SidebarLinkProps {
  item: NavItem;
  active: boolean;
  onNavigate?: (id: string) => void;
}

/** Follows a plain left click inside the app; other clicks keep the browser's behaviour. */
export function followInApp(e: MouseEvent<HTMLAnchorElement>, navigate: () => void): void {
  if (!isPlainLeftClick(e)) return;
  e.preventDefault();
  navigate();
}

/** A top-level link: its icon alone when the sidebar is collapsed, with the label as tooltip. */
export function SidebarLink({ item, active, collapsed, onNavigate }: SidebarLinkProps & { collapsed: boolean }): React.JSX.Element {
  const { anchorProps, tooltip } = useTooltip(item.label, { side: "right", disabled: !collapsed });
  return (
    <>
      <SidebarAnchor
        item={item}
        active={active}
        onNavigate={onNavigate}
        className="nb-side-item"
        ariaLabel={collapsed ? item.label : undefined}
        lead={<Icon name={item.icon} />}
        {...anchorProps}
      />
      {tooltip}
    </>
  );
}

/** A sub-section of an expanded module: a dot instead of an icon. */
export function SidebarSubLink({ item, active, onNavigate }: SidebarLinkProps): React.JSX.Element {
  return (
    <SidebarAnchor
      item={item}
      active={active}
      onNavigate={onNavigate}
      className="nb-side-item is-nested"
      lead={<span className="nb-side-dot" aria-hidden="true" />}
    />
  );
}

interface SidebarAnchorProps extends SidebarLinkProps {
  className: string;
  ariaLabel?: string;
  lead: ReactNode;
}

function SidebarAnchor({ item, active, onNavigate, className, ariaLabel, lead, ...anchorProps }: SidebarAnchorProps): React.JSX.Element {
  return (
    <a
      href={item.href ?? "#"}
      className={className}
      aria-current={active ? "page" : undefined}
      aria-label={ariaLabel}
      onClick={(e) => followInApp(e, () => onNavigate?.(item.id))}
      {...anchorProps}
    >
      {lead}
      <span className="nb-side-label">{item.label}</span>
      {item.count ? <span className="nb-count">{item.count}</span> : null}
    </a>
  );
}
